"use client";

import { ColumnDef } from "@tanstack/react-table";

import { usePermissions } from "react-admin";
import BookCustomTransfer from "./BookCustomTransfer";
import BookTransfer from "./BookTransfer";
import CommonList from "@/components/common/List";
import CommonTableList from "@/components/common/TableList";
import { Transfer } from "@/interface/entityInterface";
import { tableCol } from "@/components/common/columns/columns";
import { useState } from "react";
import TransferDetails from "./TransferDetails";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import CommonDelete from "@/components/common/Delete";
import {
  Bus,
  Car,
  CarFront,
  MoreVerticalIcon,
  Pen,
  Plus,
  Truck,
} from "lucide-react";
import TransferCU from "./Create";
import { handleRowClick } from "@/utils/functions";

const TransfersList = () => {
  const { permissions } = usePermissions();
  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [open, setOpen] = useState(false);
  const [cuOpen, setCuOpen] = useState(false);
  const [cuDetails, setCuDetails] = useState({
    mode: "",
    title: "",
    id: 0,
  });

  const openCuDialog = (
    e: React.MouseEvent,
    mode: string,
    title: string,
    id: number,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setCuOpen(true);
    setCuDetails({ mode, title, id });
  };

  const columns: ColumnDef<Transfer>[] = tableCol({
    showActions: false,
    columns: [
      {
        accessorKey: "pickup_location",
        header: "Pickup",
        cell: ({ row }) => (
          <div className="capitalize">
            <p>{row.original.pickup_location}</p>
          </div>
        ),
      },
      {
        accessorKey: "dropoff",
        header: "Dropoff",
        cell: ({ row }) => (
          <div className="capitalize">
            <p>{row.original.dropoff_location}</p>
          </div>
        ),
      },
      {
        accessorKey: "duration",
        header: "Duration",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">{row.getValue("duration")}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "price",
        header: "Price",
        cell: ({ row }) => {
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium">
                {row.getValue("price")} {row.original.currency}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "vehicle_class",
        header: "Vehicle class",
        cell: ({ row }) => {
          const vehicleClass: "economy" | "business" | "van" | "bus" =
            row.getValue("vehicle_class");
          let vehicleIcon = null;

          switch (vehicleClass) {
            case "economy":
              vehicleIcon = <Car className="w-4 h-4" />;
              break;
            case "business":
              vehicleIcon = <CarFront className="w-4 h-4" />;
              break;
            case "van":
              vehicleIcon = <Truck className="w-4 h-4" />;
              break;
            case "bus":
              vehicleIcon = <Bus className="w-4 h-4" />;
              break;
            default:
              return;
          }
          return (
            <div className="flex flex-col text-sm space-y-1">
              <span className="font-medium flex gap-x-2 items-center capitalize">{vehicleIcon} {vehicleClass}</span>
            </div>
          );
        },
      },
      ...(permissions && permissions?.user_role === "commission_partner"
        ? [
            {
              accessorKey: "book",
              header: "Book Transfer",
              cell: ({ row }: any) => (
                <BookTransfer
                  data={row.original}
                  title="Book Now"
                  description="Complete the form below to reserve your spot for this transfer."
                />
              ),
            },
          ]
        : [
            {
              accessorKey: "actions",
              cell: ({ row }: any) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                      size="icon"
                    >
                      <MoreVerticalIcon />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem
                      onClick={(e: React.MouseEvent) =>
                        openCuDialog(
                          e,
                          "edit",
                          `Edit transfer #${row.original.id}`,
                          row.original.id,
                        )
                      }
                    >
                      <Pen className="w-4 h-4" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <CommonDelete
                      id={(row.original as any).id}
                      resource={"transfers"}
                      responseMessage="Transfer deleted"
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]),
    ],
    resource: "transfers",
    responseMessage: "Transfer deleted",
  });

  return (
    <>
      <CommonList>
        <CommonTableList
          columns={columns}
          filterSearch="pickup_location"
          rowClick={(row: Transfer) => handleRowClick(row, setTransfer, setOpen)}
          create={
            permissions && permissions.user_role == "owner" ? (
              <Button
                onClick={(e: React.MouseEvent) =>
                  openCuDialog(e, "create", "Create new transfer", 0)
                }
              >
                <Plus className="h-4 w-4" />
                Transfer
              </Button>
            ) : (
              <BookCustomTransfer
                title="Book Custom Transfer"
                description="Complete the form below to reserve your spot for this transfer."
              />
            )
          }
        />
      </CommonList>
      <TransferDetails isOpen={open} setOpen={setOpen} transfer={transfer} />
      <TransferCU open={cuOpen} setOpen={setCuOpen} {...cuDetails} />
    </>
  );
};

export default TransfersList;
